import { useEffect, useState } from 'react';
import { dashboardApi } from '../services/dashboardApi';
import { useAuth } from './useAuth';

/**
 * Fetches authenticated tenant-level preferences (businessType,
 * defaultVocabularyMode, subcontractorAnchorDivision) from the
 * /user/onboarding endpoint.
 *
 * Cached at module scope so multiple components don't re-fetch on the same
 * page load. The cache is keyed by tenantId and reset whenever the active
 * tenant changes, so a prior tenant's prefs can't leak across a client-side
 * logout/login on a shared origin.
 */
let cached = null;
let cachedTenantId = null;
let inflight = null;

function unwrap(res) {
  return res?.data ?? res;
}

async function fetchOnce(tenantId) {
  if (cached && cachedTenantId === tenantId) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await dashboardApi.getOnboardingState();
      const data = unwrap(res) || {};
      cached = {
        businessType: data.businessType || null,
        defaultVocabularyMode: data.defaultVocabularyMode || null,
        subcontractorAnchorDivision: data.subcontractorAnchorDivision || null,
      };
      cachedTenantId = tenantId;
      return cached;
    } catch (err) {
      // Don't cache failures -- next caller will retry.
      console.warn('Failed to load tenant preferences:', err?.message);
      return { businessType: null, defaultVocabularyMode: null, subcontractorAnchorDivision: null };
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function useTenantPreferences() {
  const { tenantId } = useAuth();
  // Guard against the module-scope cache leaking one tenant's prefs into
  // another. The cache only clears on a full page reload, but logout/login
  // (Amplify signOut) is a client-side state change with no reload -- so on a
  // shared origin (localhost dev, non-subdomain app URL) a previous tenant's
  // prefs would otherwise persist. Reset whenever the active tenant changes.
  if (tenantId && cachedTenantId && cachedTenantId !== tenantId) {
    cached = null;
    inflight = null;
  }

  const [prefs, setPrefs] = useState(
    cachedTenantId === tenantId ? cached : null
  );

  useEffect(() => {
    if (cached && cachedTenantId === tenantId) {
      setPrefs(cached);
      return;
    }
    let cancelled = false;
    fetchOnce(tenantId).then((value) => {
      if (!cancelled) setPrefs(value);
    });
    return () => { cancelled = true; };
  }, [tenantId]);

  return prefs || { businessType: null, defaultVocabularyMode: null, subcontractorAnchorDivision: null };
}

// Test hook: clears the cache. Used by Storybook / unit tests.
export function _resetTenantPreferencesCache() {
  cached = null;
  cachedTenantId = null;
  inflight = null;
}
